/**
 * TIMED mode helpers (ADR-42) - pure, no clock.
 *
 *  - `scheduleTimedCloses`: the control-plane side. Turns a closing PATTERN
 *    (individual / staggered / grouped) into concrete per-lot `closesAt` (+
 *    `closeGroupId`) values. The reducer never sees a pattern - only times -
 *    so auction-house-specific closing rules live here and in tenant config,
 *    never inside the bid engine.
 *  - `computeExtension`: the soft-close rule the reducer applies when a bid is
 *    accepted inside the window. One place, so manual and proxy bids and cascade
 *    siblings agree by construction.
 */

import type { DomainEvent, PricingMode, TimedClosePolicy, TimedLotPhase } from './types.js';

/**
 * HipNumber PRESETS for the soft-close policy (ADR-42 decision, 2026-08-23):
 * extension behavior is tenant/sale POLICY, and these are starting points a
 * sale picks from - not product law. A tenant can still supply any custom
 * TimedClosePolicy; presets just keep the common choices consistent and named.
 *
 *  - standard-2m: the recommended default. A bid in the last 2 minutes moves the
 *    close to 2 minutes after that bid; unlimited; auto-disposition.
 *  - quick-30s: tack/equipment sessions where lots must keep moving.
 *  - hard-close: no extensions at all - the scheduled close is final.
 */
export const TIMED_CLOSE_PRESETS: Readonly<Record<'standard-2m' | 'quick-30s' | 'hard-close', TimedClosePolicy>> = {
  'standard-2m': { extensionWindowMs: 120_000, extensionDurationMs: 120_000, maxExtensions: null, autoDisposition: true, extendFrom: 'BID_TIME' },
  'quick-30s': { extensionWindowMs: 30_000, extensionDurationMs: 30_000, maxExtensions: null, autoDisposition: true, extendFrom: 'BID_TIME' },
  'hard-close': { extensionWindowMs: 0, extensionDurationMs: 0, maxExtensions: 0, autoDisposition: true, extendFrom: 'SCHEDULED_CLOSE' },
};

/**
 * ADR-70 cut 6h: what is wrong with a (partial) TimedClosePolicy override, in
 * words, or null when every field it sets is one the engine can run. A ring
 * started with RING_POLICY.timed refuses to start on a problem rather than
 * merge a value computeExtension would compare as NaN (and never extend).
 * Fields left out keep the engine's default.
 */
export function timedClosePolicyProblem(v: unknown): string | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return 'timed must be an object';
  const p = v as Record<string, unknown>;
  const ms = (k: string): boolean => p[k] === undefined || (typeof p[k] === 'number' && Number.isInteger(p[k]) && (p[k] as number) >= 0);
  if (!ms('extensionWindowMs')) return 'timed.extensionWindowMs must be whole milliseconds, 0 or more';
  if (!ms('extensionDurationMs')) return 'timed.extensionDurationMs must be whole milliseconds, 0 or more';
  const max = p['maxExtensions'];
  if (max !== undefined && max !== null && !(typeof max === 'number' && Number.isInteger(max) && max >= 0)) return 'timed.maxExtensions must be a whole number, 0 or more, or null';
  if (p['autoDisposition'] !== undefined && typeof p['autoDisposition'] !== 'boolean') return 'timed.autoDisposition must be true or false';
  if (p['extendFrom'] !== undefined && p['extendFrom'] !== 'BID_TIME' && p['extendFrom'] !== 'SCHEDULED_CLOSE') return 'timed.extendFrom must be BID_TIME or SCHEDULED_CLOSE';
  return null;
}

export type ClosingPattern = 'INDIVIDUAL' | 'STAGGERED' | 'GROUPED';

export interface ScheduleInput {
  /** Lots in run order. */
  readonly lotIds: readonly string[];
  readonly pattern: ClosingPattern;
  /** Epoch millis of the first close. */
  readonly startAt: number;
  /** STAGGERED: gap between consecutive lots. GROUPED: gap between groups. */
  readonly staggerMs?: number;
  /** GROUPED: lots per close group (they cascade-extend together). */
  readonly lotsPerGroup?: number;
}

export interface ScheduledClose {
  readonly lotId: string;
  readonly closesAt: number;
  readonly closeGroupId: string | null;
}

/**
 * INDIVIDUAL: every lot closes at `startAt`, independently (a bid on one never
 * moves another). STAGGERED: lot i closes at startAt + i*stagger, independent.
 * GROUPED: lots are chunked into groups of `lotsPerGroup`; group k closes at
 * startAt + k*stagger and the whole group cascade-extends together.
 */
export function scheduleTimedCloses(input: ScheduleInput): ScheduledClose[] {
  const stagger = Math.max(0, input.staggerMs ?? 0);
  switch (input.pattern) {
    case 'INDIVIDUAL':
      return input.lotIds.map((lotId) => ({ lotId, closesAt: input.startAt, closeGroupId: null }));
    case 'STAGGERED':
      return input.lotIds.map((lotId, i) => ({ lotId, closesAt: input.startAt + i * stagger, closeGroupId: null }));
    case 'GROUPED': {
      const per = Math.max(1, Math.floor(input.lotsPerGroup ?? 1));
      return input.lotIds.map((lotId, i) => {
        const k = Math.floor(i / per);
        return { lotId, closesAt: input.startAt + k * stagger, closeGroupId: `g${k}` };
      });
    }
  }
}

/**
 * Soft-close extension. Given a lot's current schedule and a bid accepted at
 * `now`, return the new close or null if the policy does not extend (outside the
 * window, cap reached, or the computed close would not move it later).
 */
export function computeExtension(
  lot: { readonly scheduledCloseAt: number; readonly extensions: number },
  now: number,
  policy: TimedClosePolicy,
): { readonly newCloseAt: number } | null {
  if (now >= lot.scheduledCloseAt) return null; // already closed by time - not a bid that can extend
  if (lot.scheduledCloseAt - now > policy.extensionWindowMs) return null;
  if (policy.maxExtensions !== null && lot.extensions >= policy.maxExtensions) return null;
  const from = policy.extendFrom === 'BID_TIME' ? now : lot.scheduledCloseAt;
  const newCloseAt = from + policy.extensionDurationMs;
  if (newCloseAt <= lot.scheduledCloseAt) return null;
  return { newCloseAt };
}

// ---------------------------------------------------------------------------
// ADR-69 Phase 2: the control-plane side of a timed sale, still pure.
//
//  - `planTimedOpens`: a sale's lots in run order plus its effective schedule
//    become the OPEN_TIMED_LOT bodies the ring will take, one per lot, with
//    the concrete closesAt (+ closeGroupId) from scheduleTimedCloses. The
//    worker sweep sends exactly these; the office reads the same plan to show
//    close times before the sale opens. One function, so the two agree.
//  - `timedPhasesFromEvents`: the office's view of where each lot stands,
//    folded from the ring's public events (the API Lambda reads the event
//    log from the ring store; it cannot reach the ring itself).
// ---------------------------------------------------------------------------

export interface TimedPlanLot {
  readonly lotId: string;
  /** Whole dollars. */
  readonly openingAsk: number;
  readonly pricingMode: PricingMode;
  /** Whole dollars; null = no reserve. */
  readonly reserve?: number | null;
}

export interface TimedPlanSchedule {
  /** Epoch millis of the first close. */
  readonly firstCloseAt: number;
  readonly pattern: ClosingPattern;
  readonly staggerMs: number;
  readonly lotsPerGroup: number;
}

/** Exactly the OPEN_TIMED_LOT command minus type, actor and now. */
export interface TimedOpenBody {
  readonly lotId: string;
  readonly openingAsk: number;
  readonly closesAt: number;
  readonly pricingMode: PricingMode;
  readonly reserve: number | null;
  readonly closeGroupId: string | null;
}

export type TimedOpenPlan =
  | {
      readonly ok: true;
      readonly opens: readonly TimedOpenBody[];
      readonly firstCloseAt: number;
      readonly lastCloseAt: number;
    }
  | { readonly ok: false; readonly reason: 'NO_LOTS' | 'CLOSE_IN_PAST' };

/**
 * CLOSE_IN_PAST mirrors the reducer's own refusal (closesAt must be after
 * `now`) so a plan the office can see is a plan the ring will accept.
 * Checked on the FIRST close only: with a non-negative interval every later
 * close is at or after it.
 */
export function planTimedOpens(input: {
  readonly lots: readonly TimedPlanLot[];
  readonly schedule: TimedPlanSchedule;
  readonly now: number;
}): TimedOpenPlan {
  if (input.lots.length === 0) return { ok: false, reason: 'NO_LOTS' };
  if (!(input.schedule.firstCloseAt > input.now)) return { ok: false, reason: 'CLOSE_IN_PAST' };
  const closes = scheduleTimedCloses({
    lotIds: input.lots.map((l) => l.lotId),
    pattern: input.schedule.pattern,
    startAt: input.schedule.firstCloseAt,
    staggerMs: input.schedule.staggerMs,
    lotsPerGroup: input.schedule.lotsPerGroup,
  });
  const opens = input.lots.map((lot, i) => {
    const c = closes[i]!;
    return {
      lotId: lot.lotId,
      openingAsk: lot.openingAsk,
      closesAt: c.closesAt,
      pricingMode: lot.pricingMode,
      reserve: lot.reserve ?? null,
      closeGroupId: c.closeGroupId,
    };
  });
  let last = opens[0]!.closesAt;
  for (const o of opens) if (o.closesAt > last) last = o.closesAt;
  return { ok: true, opens, firstCloseAt: opens[0]!.closesAt, lastCloseAt: last };
}

/** Where one lot stands, as far as the public event log says. */
export interface TimedLotPhaseView {
  readonly lotId: string;
  readonly phase: TimedLotPhase;
  readonly originalCloseAt: number;
  readonly scheduledCloseAt: number;
  readonly extensions: number;
  /** The standing price once SOLD, else null. */
  readonly price: number | null;
  /** ADR-70 cut 6i: the seq of the lot's last TIMED_LOT_CLOSED while it
   *  stays closed (what a timed recovery names), else null. */
  readonly closeSeq: number | null;
  /** ADR-70 cut 6i: how many times a timed recovery reopened the lot. */
  readonly reopens: number;
  /** The close group the lot was opened (or reopened) in, if any. */
  readonly closeGroupId: string | null;
}

/**
 * Folds TIMED_LOT_OPENED / LOT_CLOSE_EXTENDED / TIMED_LOT_CLOSED / LOT_SOLD /
 * LOT_NO_SALE / SALE_VOIDED / TIMED_LOT_REOPENED into a per-lot phase. A lot
 * the log never opened is absent. The public channel suffices; the reserve
 * never appears. A lot opened again after a void (its second life) starts
 * over at OPEN; a lot a timed recovery reopened (cut 6i) is OPEN again on its
 * fresh close, with its extensions starting over.
 */
export function timedPhasesFromEvents(events: readonly DomainEvent[]): Record<string, TimedLotPhaseView> {
  const out: Record<string, TimedLotPhaseView> = {};
  for (const ev of events) {
    switch (ev.type) {
      case 'TIMED_LOT_OPENED':
        out[ev.lotId] = { lotId: ev.lotId, phase: 'OPEN', originalCloseAt: ev.closesAt, scheduledCloseAt: ev.closesAt, extensions: 0, price: null, closeSeq: null, reopens: 0, closeGroupId: ev.closeGroupId };
        break;
      case 'LOT_CLOSE_EXTENDED': {
        const cur = out[ev.lotId];
        if (cur) out[ev.lotId] = { ...cur, phase: 'EXTENDED', scheduledCloseAt: ev.newCloseAt, extensions: ev.extensionNumber };
        break;
      }
      case 'TIMED_LOT_CLOSED': {
        const cur = out[ev.lotId];
        if (cur) out[ev.lotId] = { ...cur, phase: 'CLOSED_PENDING', scheduledCloseAt: ev.scheduledCloseAt, extensions: ev.extensions, closeSeq: ev.seq };
        break;
      }
      case 'LOT_SOLD': {
        const cur = out[ev.lotId];
        if (cur) out[ev.lotId] = { ...cur, phase: 'SOLD', price: ev.price };
        break;
      }
      case 'LOT_NO_SALE': {
        const cur = out[ev.lotId];
        if (cur) out[ev.lotId] = { ...cur, phase: 'NO_SALE' };
        break;
      }
      case 'SALE_VOIDED': {
        const cur = out[ev.lotId];
        if (cur) out[ev.lotId] = { ...cur, phase: 'VOIDED', price: null };
        break;
      }
      case 'TIMED_LOT_REOPENED': {
        const cur = out[ev.lotId];
        if (cur) out[ev.lotId] = { ...cur, phase: 'OPEN', originalCloseAt: ev.closesAt, scheduledCloseAt: ev.closesAt, extensions: 0, price: null, closeSeq: null, reopens: cur.reopens + 1, closeGroupId: ev.closeGroupId };
        break;
      }
      default:
        break;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// ADR-70 cut 6i (hitlist 17.4): timed recovery, the control-plane side.
//
// The office picks lots that closed with no sale and a grace of 0 to 120
// minutes. The worker turns that into REOPEN_TIMED_LOT bodies at the moment
// it sends them, so the grace counts from when the ring takes the request,
// not from when the office asked: the first fresh close is the notice plus
// the grace from then, and the rest follow the sale's own close pattern in
// the order given. The ring still sees concrete times only.
// ---------------------------------------------------------------------------

/** The longest grace the office may give, in minutes (hitlist 17.4). */
export const TIMED_RECOVERY_GRACE_MAX_MINUTES = 120;

/** Added to every grace: the time for the ring to say a lot is open again and
 *  for a bidder to see it. A grace of 0 still leaves this. */
export const TIMED_RECOVERY_NOTICE_MS = 2 * 60_000;

/** One lot of a recovery: the close it answers (the lot's closeSeq). */
export interface TimedRecoveryLot {
  readonly lotId: string;
  readonly closeSeq: number;
}

/** Exactly the REOPEN_TIMED_LOT command minus type, actor, now and the
 *  recovery's own fields (id, reason, who asked). */
export interface TimedReopenBody {
  readonly lotId: string;
  readonly closeSeq: number;
  readonly closesAt: number;
  readonly closeGroupId: string | null;
}

export type TimedRecoveryPlan =
  | { readonly ok: true; readonly reopens: readonly TimedReopenBody[]; readonly firstCloseAt: number; readonly lastCloseAt: number }
  | { readonly ok: false; readonly reason: 'NO_LOTS' | 'GRACE_OUT_OF_RANGE' };

/** Whether a grace is whole minutes from 0 to the most the office may give. */
export function isTimedRecoveryGrace(minutes: unknown): minutes is number {
  return typeof minutes === 'number' && Number.isInteger(minutes) && minutes >= 0 && minutes <= TIMED_RECOVERY_GRACE_MAX_MINUTES;
}

/** A recovery's close groups are its own: a reopened lot never extends with a
 *  lot of the sale's first run that happens to share a group number. */
export function timedRecoveryGroupPrefix(recoveryId: string): string {
  return `r${recoveryId.replace(/[^A-Za-z0-9]/g, '').slice(0, 8)}-`;
}

export function planTimedRecovery(input: {
  readonly lots: readonly TimedRecoveryLot[];
  readonly schedule: { readonly pattern: ClosingPattern; readonly staggerMs: number; readonly lotsPerGroup: number };
  readonly graceMinutes: number;
  readonly now: number;
  readonly recoveryId: string;
}): TimedRecoveryPlan {
  if (input.lots.length === 0) return { ok: false, reason: 'NO_LOTS' };
  if (!isTimedRecoveryGrace(input.graceMinutes)) return { ok: false, reason: 'GRACE_OUT_OF_RANGE' };
  const startAt = input.now + TIMED_RECOVERY_NOTICE_MS + input.graceMinutes * 60_000;
  const closes = scheduleTimedCloses({
    lotIds: input.lots.map((l) => l.lotId),
    pattern: input.schedule.pattern,
    startAt,
    staggerMs: input.schedule.staggerMs,
    lotsPerGroup: input.schedule.lotsPerGroup,
  });
  const prefix = timedRecoveryGroupPrefix(input.recoveryId);
  const reopens = input.lots.map((lot, i) => {
    const c = closes[i]!;
    return { lotId: lot.lotId, closeSeq: lot.closeSeq, closesAt: c.closesAt, closeGroupId: c.closeGroupId === null ? null : prefix + c.closeGroupId };
  });
  let last = reopens[0]!.closesAt;
  for (const r of reopens) if (r.closesAt > last) last = r.closesAt;
  return { ok: true, reopens, firstCloseAt: reopens[0]!.closesAt, lastCloseAt: last };
}

/**
 * Why a lot cannot be part of a timed recovery, in the words the office reads
 * (null = it can). Judged from where the lot stands, the public log's view or
 * the ring's own: only a lot that closed with no sale reopens. `closeSeq`,
 * when given, is the close the request names; a lot that reopened or closed
 * again since then has CHANGED_SINCE. The reducer's timedReopenRefusal is the
 * rule; this is the same rule worded per phase.
 */
export type TimedRecoveryBlocker = 'NOT_OPENED' | 'SOLD' | 'STILL_OPEN' | 'AWAITING_STAFF' | 'VOIDED' | 'CHANGED_SINCE';

export function timedRecoveryBlocker(lot: { readonly phase: TimedLotPhase; readonly closeSeq: number | null } | undefined, closeSeq?: number): TimedRecoveryBlocker | null {
  if (!lot) return 'NOT_OPENED';
  switch (lot.phase) {
    case 'SOLD': return 'SOLD';
    case 'OPEN':
    case 'EXTENDED': return 'STILL_OPEN';
    case 'CLOSED_PENDING': return 'AWAITING_STAFF';
    case 'VOIDED': return 'VOIDED';
    case 'NO_SALE':
      if (lot.closeSeq === null) return 'CHANGED_SINCE';
      return closeSeq !== undefined && closeSeq !== lot.closeSeq ? 'CHANGED_SINCE' : null;
  }
}
